import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../utils/useTranslation';
import { Skeleton } from '../feedback/Skeleton';
import { Tooltip } from '../feedback/Tooltip';
import { IconHelp } from '../icon/IconHelp';
import { Button } from '../input/Button';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { BusinessStatsUI } from './types';

interface StatItemProps {
    title: string;
    displayValue: string;
    diffValue: number;
    diffDisplay: string;
}

const StatItem = (props: StatItemProps) => {
    const { title, displayValue, diffValue, diffDisplay } = props;

    return (
        <Flex width="calc(100% / 3)" spacing={0.5}>
            <Typography variant="caption" color="secondary">
                {title}
            </Typography>
            <Flex direction="row" spacing={1} alignItems="end">
                <Typography variant="title3" weight="bold">
                    {displayValue}
                </Typography>
                {diffValue ? (
                    <Typography
                        variant="caption"
                        color={diffValue >= 0 ? 'var(--color-success)' : 'var(--color-danger)'}
                    >
                        {diffDisplay}
                    </Typography>
                ) : (
                    ''
                )}
            </Flex>
        </Flex>
    );
};

const StatItemSkeleton = () => (
    <Flex width="calc(100% / 3)" spacing={0.5}>
        <Skeleton width="80px" variant="text" height={14} />
        <Flex direction="row" spacing={1} alignItems="end">
            <Skeleton width="90px" variant="text" height={24} />
        </Flex>
    </Flex>
);

export const BusinessStatisticSkeleton = () => (
    <Box py={1} px={3}>
        <Flex background="var(--background-secondary)" width="100%" borderRadius={2} px={2} pb={2}>
            <Flex width="100%" direction="row" alignItems="center" justifyContent="space-between">
                <Box py={2}>
                    <Skeleton width="100px" variant="text" height={20} />
                </Box>
                <Skeleton variant="rect" width={125} height={32} borderRadius="12px" />
            </Flex>
            <Flex width="100%" direction="row" flexWrap="wrap">
                <StatItemSkeleton />
                <StatItemSkeleton />
                <StatItemSkeleton />
            </Flex>
        </Flex>
    </Box>
);

interface BusinessStatisticProps {
    statistic: BusinessStatsUI;
    statisticUrl: string;
    onAllStatisticClick: () => void;
}

export const BusinessStatistic = (props: BusinessStatisticProps) => {
    const t = useTranslation();
    const { statistic, statisticUrl, onAllStatisticClick } = props;
    const { coverage, subscribers, interactions } = statistic;

    return (
        <Box py={1} px={{ xs: 2, sm: 3 }}>
            <Flex
                background="var(--background-secondary)"
                width="100%"
                borderRadius={2}
                px={2}
                pb={2}
            >
                <Flex
                    width="100%"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Box py={2}>
                        <Flex direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body" weight="bold">
                                {t('Статистика')}
                            </Typography>
                            <Tooltip
                                trigger={
                                    <IconHelp
                                        color="var(--color-primary)"
                                        cursor="help"
                                        size={16}
                                    />
                                }
                            >
                                <Typography
                                    variant="caption"
                                    weight="bold"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {'Охват'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {
                                        ' - количество уникальных аккаунтов, владельцы которых хотя бы 1 раз видели ваш контент за последние 7 дней, с изменением в процентах относительно предыдущих 7 дней. Эта метрика находится в разработке и возможны изменения в расчетах.'
                                    }
                                </Typography>
                                <br />
                                <br />
                                <Typography
                                    variant="caption"
                                    weight="bold"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {'Подписчики (за весь срок существования профиля)'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {
                                        ' - количество аккаунтов, подписавшихся на профиль за весь период с изменением в количестве подписавшихся за последние 7 дней.'
                                    }
                                </Typography>
                                <br />
                                <br />
                                <Typography
                                    variant="caption"
                                    weight="bold"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {'Клики по ссылкам'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {
                                        ' - количество переходов по ссылкам из контента и из профиля за последние 7 дней и изменение в процентах относительно предыдущих 7 дней'
                                    }
                                </Typography>
                                <br />
                                <br />
                                <Typography
                                    variant="caption"
                                    weight="bold"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {'Вся статистика'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {
                                        ' - переход на расширенную аналитику с возможностью посмотреть статистику за разные периоды и за все время существования профиля.'
                                    }
                                </Typography>
                            </Tooltip>
                        </Flex>
                    </Box>
                    <Button
                        as={Link}
                        variant="filled"
                        color="invert"
                        to={statisticUrl}
                        textTransform="unset"
                        borderRadius="12px"
                        onClick={onAllStatisticClick}
                    >
                        Вся статистика
                    </Button>
                </Flex>
                <Flex width="100%" direction="row" flexWrap="wrap">
                    <StatItem
                        title={t('Охват')}
                        displayValue={coverage.total.display}
                        diffValue={coverage.total.diffValue}
                        diffDisplay={coverage.total.diffDisplay}
                    />
                    <StatItem
                        title={t('Подписчики')}
                        displayValue={subscribers.total.display}
                        diffValue={subscribers.total.diffValue}
                        diffDisplay={subscribers.total.diffDisplay}
                    />
                    <StatItem
                        title={t('Клики по ссылке')}
                        displayValue={interactions.clicks.display}
                        diffValue={interactions.clicks.diffValue}
                        diffDisplay={interactions.clicks.diffDisplay}
                    />
                </Flex>
            </Flex>
        </Box>
    );
};
